import { IonDatetime, IonInput, IonItem, IonLabel } from '@ionic/react';
import { StethoscopeIcon } from '@phosphor-icons/react';
import React from 'react';
import { useForm } from 'react-hook-form';

import { usePets } from '../../../context/PetsContext';
import { useSVFs } from '../../../context/SVFsContext';
import { useSiniestros } from '../../../context/SiniestrosContext';
import { useVetFormSend } from '../../../hooks/Reintegro/useVetFormSend';
import { useReSendVetFormLink } from '../../../hooks/Reintegro/useVeterinario';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { iconSiniestroProps } from '../../../pages/siniestro';
import {
  VetFormLinkData,
  VeterinarioForm as VeterinarioFormType,
  getEmptyVeterinarioForm,
} from '../../../types/Reintegro/Veterinario/types';
import { today } from '../../../utils/date';
import { capitalizeEveryWord } from '../../../utils/string';
import { validations } from '../../../utils/validations';
import Button from '../../UI/Button';
import FormHeader from '../../UI/FormHeader';
import FormItem from '../../UI/FormItem';
import Spinner from '../../UI/Spinner';
import './VeterinarioForm.scss';
import { VeterinarioFormProps } from './types';

const VeterinarioForm: React.FC<VeterinarioFormProps> = ({
  onNext,
  showAlert,
  submittedVetValues,
  buttonLabel = 'Siguiente',
  isEditMode = false,
  vetFormId,
}) => {
  const { ref, handleEnter } = useEnter();
  const { currentSiniestro } = useSiniestros();
  const { vetFormId_Ref, mutateSVFs } = useSVFs();
  const { currentPet } = usePets();

  const { submit, isLoading: isSubmitLoading } = useVetFormSend({
    currentSiniestroId: currentSiniestro!.id,
    vetFormIdRef: vetFormId_Ref,
    submittedVetValues,
    onNext,
    showAlert,
    mutateSVFs,
  });

  const { reSendVetFormLink, isLoading: isReSendLoading } = useReSendVetFormLink();

  const isLoading = isSubmitLoading || isReSendLoading;

  const { handleSubmit, control, errors } = useForm<VeterinarioFormType>({
    defaultValues: submittedVetValues.current ?? getEmptyVeterinarioForm(),
  });

  const buildBody = (values: VeterinarioFormType): VetFormLinkData => {
    const nombre = isEditMode && submittedVetValues.current
      ? `${capitalizeEveryWord(submittedVetValues.current.nombre)} ${capitalizeEveryWord(submittedVetValues.current.apellido)}`.trim()
      : `${capitalizeEveryWord(values.nombre)} ${capitalizeEveryWord(values.apellido)}`.trim();

    return {
      veterinario: {
        nombre,
        mail: values.email.trim(),
        telefono: values.telefono,
        tipo: 'VetContact',
      },
      siniestroId: currentSiniestro!.id,
      fechaAtencion: values.fechaAtencion,
      derivaciones: [],
    };
  };

  const onSubmit = async (values: VeterinarioFormType) => {
    const body = buildBody(values);

    console.log('[VeterinarioForm] submit', {
      isEditMode,
      vetFormId,
      currentSiniestro,
      currentPet,
      vetFormIdRef: vetFormId_Ref.current,
      submittedVetValues: submittedVetValues.current,
      values,
      body,
    });

    // En modo edición solo enviamos email y telefono
    if (isEditMode && vetFormId) {
      const prev = submittedVetValues.current;

      // Verificar si hay cambios en los campos editables (email y telefono)
      const hasChanges =
        !prev ||
        values.email.trim() !== prev.email.trim() ||
        values.telefono !== prev.telefono;

      // Si no hay cambios, solo cerrar sin enviar al backend
      if (!hasChanges) {
        onNext();
        return;
      }

      console.log('[VeterinarioForm] resend update', {
        vetFormId,
        body,
        oldEmail: prev?.email,
        newEmail: values.email,
        oldPhone: prev?.telefono,
        newPhone: values.telefono
      });

      const res = await reSendVetFormLink({
        vetFormLinkData: body,
        subVetFormId: vetFormId,
      });

      if (!res.ok) {
        showAlert({
          header: 'Error',
          message: 'No se pudo actualizar los datos del veterinario.',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
        return;
      }

      submittedVetValues.current = {
        nombre: submittedVetValues.current?.nombre ?? '',
        apellido: submittedVetValues.current?.apellido ?? '',
        email: values.email,
        telefono: values.telefono,
        fechaAtencion: values.fechaAtencion,
      };
      await mutateSVFs();

      showAlert({
        header: 'Datos actualizados',
        message: 'Se actualizaron los datos de contacto del veterinario.',
        buttons: [{ text: 'Entendido', handler: onNext }],
      });
      return;
    }

    // Flujo normal (creación)
    await submit(values, body);
  };

  const formFields = isEditMode
    ? [
        {
          name: 'nombreCompleto',
          label: 'Nombre',
          disabled: true,
          isReadOnly: true,
        },
        {
          name: 'email',
          label: 'Email',
          pattern: validations.email,
          requiredMessage: 'Por favor, ingrese el email',
          disabled: false,
        },
        {
          name: 'telefono',
          label: 'Teléfono',
          pattern: validations.phone,
          requiredMessage: 'Por favor, ingrese el teléfono',
          disabled: false,
        },
        {
          name: 'fechaAtencion',
          label: 'Fecha de atención',
          isDate: true,
          requiredMessage: 'Por favor, ingrese la fecha',
          disabled: true,
        },
      ]
    : [
        {
          name: 'nombre',
          label: 'Nombre',
          pattern: validations.name,
          transform: true,
          requiredMessage: 'Por favor, ingrese el nombre',
          disabled: false,
        },
        {
          name: 'apellido',
          label: 'Apellido',
          pattern: validations.name,
          transform: true,
          requiredMessage: 'Por favor, ingrese el apellido',
          disabled: false,
        },
        {
          name: 'email',
          label: 'Email',
          pattern: validations.email,
          requiredMessage: 'Por favor, ingrese el email',
          disabled: false,
        },
        {
          name: 'telefono',
          label: 'Teléfono',
          pattern: validations.phone,
          requiredMessage: 'Por favor, ingrese el teléfono',
          disabled: false,
        },
        {
          name: 'fechaAtencion',
          label: 'Fecha de atención',
          isDate: true,
          requiredMessage: 'Por favor, ingrese la fecha',
          disabled: false,
        },
      ];

  const nombreCompleto = submittedVetValues.current
    ? `${submittedVetValues.current.nombre} ${submittedVetValues.current.apellido}`.trim()
    : '';

  return (
    <div className="veterinario-form ion-padding">
      <FormHeader
        icon={<StethoscopeIcon {...iconSiniestroProps} />}
        title="Veterinario"
        subtitle={`Profesional que atendió a ${currentPet?.nombre}`}
      />

      <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
        {formFields.map((field) =>
          field.isReadOnly ? (
            <IonItem key={field.name}>
              <IonLabel position="floating">{field.label}</IonLabel>
              <IonInput value={nombreCompleto} disabled />
            </IonItem>
          ) : (
            <FormItem
              key={field.name}
              control={control}
              name={field.name as keyof VeterinarioFormType}
              errors={errors}
              rules={{
                required: field.requiredMessage,
                ...(field.pattern && { pattern: field.pattern }),
              }}
              render={({ onChange, value, name }) =>
                field.isDate ? (
                  <IonItem>
                    <IonLabel position="floating">{field.label}</IonLabel>
                    <IonDatetime
                      value={value}
                      onIonChange={onChange}
                      name={name}
                      max={today}
                      displayFormat="DD/MM/YYYY"
                      doneText="Confirmar"
                      cancelText="Cancelar"
                      disabled={field.disabled}
                    />
                  </IonItem>
                ) : (
                  <IonItem>
                    <IonLabel position="floating">{field.label}</IonLabel>
                    <IonInput
                      name={name}
                      value={value ?? ''}
                      onIonChange={(e) => onChange(e.detail.value ?? '')}
                      onIonBlur={() =>
                        value && field.transform && onChange(capitalizeEveryWord(value))
                      }
                      disabled={field.disabled}
                    />
                  </IonItem>
                )
              }
            />
          )
        )}

        <div className="ion-margin-top">
          <Button ref={ref} type="submit" disabled={isLoading}>
            {isLoading ? <Spinner color="light" /> : buttonLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(VeterinarioForm);
